import {
  Box,
  DrawerOverlay,
  forwardRef,
  DrawerContent as ChakraDrawerContent,
  DrawerContentProps,
} from '@chakra-ui/react';
import { useMemo, useRef } from 'react';

import { OverlayContainerRefContextProvider } from '../OverlayContainerRefContext';
import { useDrawerContext } from './DrawerContext';

const DEFAULT_NESTED_DRAWER_SHIFT_AMOUNT = '-16.75rem';

const translate = (level = 0, amount = DEFAULT_NESTED_DRAWER_SHIFT_AMOUNT) =>
  level > 0 && amount ? `calc(${level} * ${amount})` : 0;

const transition = {
  duration: 0.2,
  ease: [0.8, 0, 0.3, 1.1],
};

const variants = {
  exit: () => ({
    x: '100%',
    transition,
  }),
  enter: (level = 0) => ({
    x: translate(level),
    transition,
  }),
};

/**
 * ModalContent is used to group modal's content. It has all the
 * necessary `aria-*` properties to indicate that it is a modal
 */
export const DrawerContent = forwardRef(
  ({ motionProps, children, ...restProps }: DrawerContentProps, ref) => {
    const { level } = useDrawerContext();

    const containerRef = useRef<HTMLDivElement>(null);

    const containerRefContextValue = useMemo(
      () => ({
        containerRef,
      }),
      [containerRef],
    );

    return (
      <>
        <DrawerOverlay />
        <ChakraDrawerContent
          motionProps={{ custom: level, variants, ...motionProps }}
          ref={ref}
          {...restProps}
        >
          <Box ref={containerRef} display="contents">
            <OverlayContainerRefContextProvider
              value={containerRefContextValue}
            >
              {children}
            </OverlayContainerRefContextProvider>
          </Box>
        </ChakraDrawerContent>
      </>
    );
  },
);
DrawerContent.displayName = 'DrawerContent';
